/**
 * LPO email generator modal (clinic orders page).
 *
 * CLIPBOARD FEATURE, NOT A TRANSACTIONAL EMAIL: this composes an English note
 * and copies it. Nothing here goes through send-email, Resend or the
 * notification subscriptions, by design.
 *
 * The whole interaction: pick a format (one row today), tick the orders,
 * Copy, Close (button or click outside). No sending, no editing, no
 * per-order actions.
 *
 * The order list is the page's OWN list query (orderQueries.ordersListQuery)
 * run with the page's own scope parameters, so a podiatrist sees exactly
 * what the page shows them and an admin sees all, by construction. Two
 * decorating reads (ledger rows, patient privacy flags) run over ids that
 * list already surfaced, each under its own table's RLS; nothing widens the
 * caller's view. Drafts, cancelled and returned are excluded; newest first.
 *
 * The ledger is written ONLY after the clipboard write resolves, never on
 * open; nothing is preselected, so Copy stays disabled until at least one
 * order is ticked; already-included orders show a badge and a non-blocking
 * warning when ticked anyway.
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Mail } from 'lucide-react';
import {
  Badge,
  Button,
  Modal,
  ModalContent,
  ModalDescription,
  ModalHeader,
  ModalTitle,
  Skeleton,
  StatusBadge,
} from '@insolech/ui';
import type { StatusKey } from '@insolech/ui';
import { lpoEmailQueries, orderQueries, type LpoLedgerEntry, type OrderListRow } from '@insolech/sdk';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthContext';
import { errorMessage } from '@/lib/errorMessage';
import { formatProductLabel } from '../productLabel';
import {
  LPO_EMAIL_FORMATS,
  firstNameOf,
  lpoPatientLabel,
  type LpoEmailOrder,
  type LpoEmailOutput,
} from './lpoEmailFormats';
import { copyPlainText, copyRichText } from './clipboard';

type ListParams = orderQueries.OrderListParams;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The page's current list parameters; reused verbatim except for paging,
   *  status and search, which the modal owns. */
  listParams: ListParams;
}

// Same page-local mapping the tracker, dashboard and detail page each carry.
const STATUS_TO_KEY: Record<string, StatusKey> = {
  draft: 'draft',
  submitted: 'submitted',
  in_review: 'in_review',
  in_production: 'in_production',
  in_delivery: 'in_delivery',
  delivered: 'delivered',
  cancelled: 'cancelled',
  returned: 'returned',
  on_hold: 'on_hold',
};

const CHECKBOX_CLASS =
  'h-4 w-4 rounded border-border-default text-brand-600 focus:ring-brand-500 focus:ring-offset-0';

/** Never sent to procurement: unfinished or reversed orders. */
const EXCLUDED_STATUSES = new Set(['draft', 'cancelled', 'returned']);

/** MIRROR of the page's status chips: same keys, same labels, same
 *  ENUM_BY_FILTER groupings (the page's source of truth), in the page's
 *  order, minus 'total' (the modal's All already means no status filter)
 *  and minus 'pending' (pending = draft ONLY, and drafts are excluded from
 *  this list by design; the ledger insert policy refuses them, so the
 *  option would be permanently empty). In review and on hold belong to no
 *  chip on the page, so here they surface under All only. Exported for the
 *  drift test that compares this list against the page's FILTER_TABS. */
export const MODAL_STATUS_FILTERS: ReadonlyArray<{
  key: orderQueries.OrderStatusFilter;
  label: string;
}> = [
  { key: 'active', label: 'Active' },
  { key: 'submitted', label: 'Submitted' },
  { key: 'production', label: 'Production' },
  { key: 'delivery', label: 'Delivery' },
  { key: 'delivered', label: 'Delivered' },
];

/** The page's own export precedent: the same query, first page, wide page. */
const LPO_PAGE_SIZE = 5000;

function formatBadgeDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso.slice(0, 10);
  }
}

export function LpoEmailModal({ open, onOpenChange, listParams }: Props): React.ReactElement {
  const { t } = useTranslation(['orders']);
  const { user, fullName } = useAuth();
  const queryClient = useQueryClient();

  const [formatKey, setFormatKey] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState('');
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = React.useState<string>('all');
  const [status, setStatus] = React.useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [copiedSubject, setCopiedSubject] = React.useState(false);

  const format = formatKey ? (LPO_EMAIL_FORMATS.find((f) => f.key === formatKey) ?? null) : null;

  // The page's query, the page's scope. Only paging, status and search differ.
  const ordersQuery = useQuery({
    ...orderQueries.ordersListQuery(supabase, {
      ...listParams,
      status: 'total',
      search: undefined,
      page: 0,
      pageSize: LPO_PAGE_SIZE,
      sortBy: 'created_at',
      sortDir: 'desc',
    }),
    enabled: open && format !== null,
    staleTime: 0,
  });

  const rows = React.useMemo<OrderListRow[]>(
    () => (ordersQuery.data?.rows ?? []).filter((r) => !EXCLUDED_STATUSES.has(r.status)),
    [ordersQuery.data],
  );
  const orderIds = React.useMemo(() => rows.map((r) => r.id), [rows]);
  const patientIds = React.useMemo(
    () => Array.from(new Set(rows.map((r) => r.patient?.id).filter((x): x is string => !!x))),
    [rows],
  );

  const ledgerQuery = useQuery({
    ...lpoEmailQueries.lpoLedgerForOrders(supabase, orderIds),
    enabled: open && orderIds.length > 0,
    staleTime: 0,
  });
  const privacyQuery = useQuery({
    ...lpoEmailQueries.patientPrivacyFlags(supabase, patientIds),
    enabled: open && patientIds.length > 0,
    staleTime: 0,
  });

  const ledgerByOrder = React.useMemo(() => {
    const m = new Map<string, LpoLedgerEntry>();
    for (const e of ledgerQuery.data ?? []) if (!m.has(e.order_id)) m.set(e.order_id, e); // newest first
    return m;
  }, [ledgerQuery.data]);
  const privacyByPatient = React.useMemo(() => {
    const m = new Map<string, { is_private: boolean | null; patient_code: string | null }>();
    for (const p of privacyQuery.data ?? []) m.set(p.id, p);
    return m;
  }, [privacyQuery.data]);

  const toFormatOrder = React.useCallback(
    (r: OrderListRow): LpoEmailOrder => {
      const flags = r.patient ? privacyByPatient.get(r.patient.id) : undefined;
      return {
        id: r.id,
        order_number: r.order_number,
        created_at: r.created_at,
        patient: r.patient
          ? {
              full_name: r.patient.full_name,
              is_private: flags?.is_private ?? null,
              patient_code: flags?.patient_code ?? null,
            }
          : null,
        lines: r.lines,
      };
    },
    [privacyByPatient],
  );

  // Reset on every open; nothing is written on open.
  React.useEffect(() => {
    if (!open) return;
    setFormatKey(null);
    setSearch('');
    setSelected(new Set());
    setStatusFilter('all');
    setStatus(null);
    setCopiedSubject(false);
  }, [open]);

  // No preselection: opening the modal selects NOTHING, and Copy stays
  // disabled until the user ticks at least one order (output is null while
  // the selection is empty).

  const visibleRows = React.useMemo(() => {
    const byStatus =
      statusFilter === 'all'
        ? rows
        : rows.filter((r) =>
            (orderQueries.ENUM_BY_FILTER[statusFilter as orderQueries.OrderStatusFilter] ?? []).includes(
              r.status,
            ),
          );
    const q = search.trim().toLowerCase();
    if (!q) return byStatus;
    return byStatus.filter((r) => {
      const hay = [
        r.order_number,
        lpoPatientLabel(toFormatOrder(r)),
        ...r.lines.map((l) => formatProductLabel(l)),
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rows, statusFilter, search, toFormatOrder]);

  const selectedRows = React.useMemo(() => rows.filter((r) => selected.has(r.id)), [rows, selected]);
  // Already-included orders the user ticked anyway: allowed, but warned.
  const selectedIncludedCount = React.useMemo(
    () => selectedRows.filter((r) => ledgerByOrder.has(r.id)).length,
    [selectedRows, ledgerByOrder],
  );

  const output: LpoEmailOutput | null = React.useMemo(() => {
    if (!format || selectedRows.length === 0) return null;
    return format.build(selectedRows.map(toFormatOrder), {
      podiatristFirstName: firstNameOf(fullName) || (user?.email?.split('@')[0] ?? ''),
    });
  }, [format, selectedRows, toFormatOrder, fullName, user?.email]);

  const toggle = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const copyMutation = useMutation({
    mutationFn: async () => {
      if (!format || !output || selectedRows.length === 0) return null;
      // 1. Clipboard first (text/plain + text/html, plain fallback). If this
      //    throws, no ledger row is written and nothing changes.
      await copyRichText(output.text, output.html);
      // 2. Only after the clipboard write resolved: one ledger row per order.
      try {
        await lpoEmailQueries.recordLpoEmailGeneration(supabase, {
          generationId: crypto.randomUUID(),
          formatKey: format.key,
          orders: selectedRows.map((r) => ({ id: r.id, clinic_id: r.clinic_id })),
        });
      } catch (err) {
        return { copied: true, ledgerError: errorMessage(err) };
      }
      return { copied: true, ledgerError: null as string | null };
    },
    onSuccess: async (result) => {
      if (!result) return;
      if (result.ledgerError) {
        setStatus({ tone: 'error', text: t('orders:lpo_ledger_failed', { error: result.ledgerError }) });
      } else {
        setStatus({ tone: 'ok', text: t('orders:lpo_copied_email') });
      }
      await queryClient.invalidateQueries({ queryKey: lpoEmailQueries.lpoEmailKeys.all });
    },
    onError: () => {
      // Clipboard unavailable: the text stays selectable on screen for a
      // manual copy; no ledger row was written.
      setStatus({ tone: 'error', text: t('orders:lpo_copy_failed') });
    },
  });

  const copySubject = async (): Promise<void> => {
    if (!output) return;
    const ok = await copyPlainText(output.subject);
    setCopiedSubject(ok);
    if (ok) window.setTimeout(() => setCopiedSubject(false), 1500);
    else setStatus({ tone: 'error', text: t('orders:lpo_copy_failed') });
  };

  const loading = ordersQuery.isLoading || (orderIds.length > 0 && ledgerQuery.isLoading);

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="lg">
        <ModalHeader>
          <ModalTitle>{t('orders:lpo_modal_title')}</ModalTitle>
          <ModalDescription>{t('orders:lpo_modal_description')}</ModalDescription>
        </ModalHeader>

        <div className="px-6 pb-4 flex flex-col gap-4">
          {/* Format registry: one row per format, the user picks one. */}
          <ul className="rounded-md border border-border-subtle divide-y divide-border-subtle" role="list">
            {LPO_EMAIL_FORMATS.map((f) => {
              const picked = f.key === formatKey;
              return (
                <li key={f.key}>
                  <button
                    type="button"
                    className={`flex w-full items-center gap-3 px-3 py-2 text-start text-sm ${
                      picked ? 'bg-brand-50 text-brand-800' : 'hover:bg-surface-muted text-text-primary'
                    }`}
                    aria-pressed={picked}
                    onClick={() => setFormatKey(f.key)}
                    data-testid={`lpo-format-${f.key}`}
                  >
                    <Mail className="h-4 w-4 shrink-0" />
                    <span className="font-medium">{f.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>

          {format ? (
            <>
              <input
                type="search"
                className="w-full rounded-md border border-border-default bg-surface px-3 py-1.5 text-sm"
                placeholder={t('orders:lpo_search_placeholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="lpo-search"
              />

              <div className="flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-1.5 text-sm">
                  <span className="text-xs text-text-secondary">{t('orders:lpo_status_filter_label')}</span>
                  <select
                    className="rounded-md border border-border-default bg-surface px-2 py-1 text-xs"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    data-testid="lpo-status-filter"
                  >
                    <option value="all">{t('orders:lpo_status_filter_all')}</option>
                    {MODAL_STATUS_FILTERS.map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                {/* Select all takes ONLY what the active filter + search show,
                    never the full list behind them; the count in the label
                    says how many that is before the click. */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setSelected((prev) => {
                      const next = new Set(prev);
                      for (const r of visibleRows) next.add(r.id);
                      return next;
                    })
                  }
                  disabled={visibleRows.length === 0}
                  data-testid="lpo-select-all"
                >
                  {t('orders:lpo_select_all', { count: visibleRows.length })}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelected(new Set())}
                  disabled={selectedRows.length === 0}
                  data-testid="lpo-clear-selection"
                >
                  {t('orders:lpo_clear_selection')}
                </Button>
                <span className="ms-auto text-xs text-text-muted" data-testid="lpo-selected-count">
                  {t('orders:lpo_selected_count', { count: selectedRows.length })}
                </span>
              </div>

              <div className="max-h-[32vh] overflow-y-auto rounded-md border border-border-subtle">
                {loading ? (
                  <div className="p-3 flex flex-col gap-2">
                    <Skeleton className="h-5 w-full" />
                    <Skeleton className="h-5 w-3/4" />
                  </div>
                ) : ordersQuery.isError ? (
                  <p className="p-3 text-sm text-error-700">{errorMessage(ordersQuery.error)}</p>
                ) : visibleRows.length === 0 ? (
                  <p className="p-3 text-sm text-text-muted">{t('orders:lpo_empty')}</p>
                ) : (
                  <ul className="divide-y divide-border-subtle">
                    {visibleRows.map((row) => {
                      const included = ledgerByOrder.get(row.id);
                      const fo = toFormatOrder(row);
                      return (
                        <li key={row.id} className="flex items-start gap-3 px-3 py-2">
                          <input
                            type="checkbox"
                            className={`${CHECKBOX_CLASS} mt-1`}
                            checked={selected.has(row.id)}
                            onChange={() => toggle(row.id)}
                            aria-label={row.order_number}
                            data-testid={`lpo-order-${row.order_number}`}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2 text-sm">
                              <span className="font-medium text-text-primary">{row.order_number}</span>
                              <StatusBadge
                                status={(STATUS_TO_KEY[row.status] ?? 'draft') as StatusKey}
                                size="sm"
                              />
                              <span className="text-text-secondary">{lpoPatientLabel(fo)}</span>
                              {included ? (
                                <Badge variant="warning" size="sm">
                                  {included.generated_by_profile?.full_name
                                    ? t('orders:lpo_badge_included', {
                                        name: included.generated_by_profile.full_name,
                                        date: formatBadgeDate(included.generated_at),
                                      })
                                    : t('orders:lpo_badge_included_unknown', {
                                        date: formatBadgeDate(included.generated_at),
                                      })}
                                </Badge>
                              ) : null}
                            </div>
                            <p className="mt-0.5 truncate text-xs text-text-muted">
                              {row.lines.map((l) => formatProductLabel(l)).join(' · ')}
                            </p>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {/* Duplicate warning: non-blocking, Copy stays enabled. */}
              {selectedIncludedCount > 0 ? (
                <p className="text-xs text-warning-700" role="status" data-testid="lpo-duplicate-warning">
                  {t('orders:lpo_duplicate_warning', { count: selectedIncludedCount })}
                </p>
              ) : null}

              {/* The generated email: subject with its own copy control, body as
                  plain selectable text. */}
              {output ? (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <span className="shrink-0 text-xs text-text-secondary">{t('orders:lpo_subject_label')}</span>
                    <code
                      className="min-w-0 flex-1 truncate rounded bg-surface-muted px-2 py-1 text-xs select-text"
                      title={output.subject}
                      data-testid="lpo-subject"
                    >
                      {output.subject}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void copySubject()}
                      startIcon={copiedSubject ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      data-testid="lpo-copy-subject"
                    >
                      {copiedSubject ? t('orders:lpo_copied_subject') : t('orders:lpo_copy_subject')}
                    </Button>
                  </div>
                  <pre
                    className="max-h-[28vh] overflow-auto whitespace-pre-wrap rounded-md border border-border-subtle bg-surface-muted p-3 font-sans text-sm text-text-primary select-text"
                    data-testid="lpo-email-text"
                  >
                    {output.text}
                  </pre>
                </div>
              ) : null}

              {status ? (
                <p
                  className={`text-xs ${status.tone === 'ok' ? 'text-success-700' : 'text-error-700'}`}
                  role="status"
                >
                  {status.text}
                </p>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-text-muted">{t('orders:lpo_pick_format')}</p>
          )}
        </div>

        <div className="px-6 pb-6 pt-2 flex items-center justify-end gap-2 border-t border-border-subtle">
          <Button
            variant="outline"
            size="md"
            onClick={() => onOpenChange(false)}
            disabled={copyMutation.isPending}
            data-testid="lpo-close"
          >
            {t('orders:lpo_close')}
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => copyMutation.mutate()}
            disabled={!output || copyMutation.isPending}
            data-testid="lpo-copy-email"
          >
            {t('orders:lpo_copy_email')}
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
}
